import SlideFrame from "@/components/SlideFrame";
import { Cloud, Database, KeyRound, ShieldCheck, Check, Scale, Lock, Brain } from "lucide-react";
import { isLegalPrimary, packFor } from "@/lib/industryPack";

/* ============================ 05 · Closed Systems / Architecture ============================ */
export function S_ClosedSystems({ idx, total, prospect }) {
  const pack = packFor(prospect);
  const corp = pack.corporate;
  const legalPrimary = isLegalPrimary(prospect);
  return (
    <SlideFrame idx={idx} total={total} kicker={legalPrimary ? "Privilege Architecture" : "Closed Systems"} dark>
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-6">
          <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-5">
            {legalPrimary ? "/ privilege · matter file · architectural protection" : "/ your data, your bucket, your gates"}
          </p>
          <h2 className="serif font-light text-5xl md:text-6xl leading-[1.04] tracking-tight">
            {legalPrimary ? (
              <>Your matter file<br /><em className="italic text-sun-600">stays privileged.</em></>
            ) : (
              <>AI without<br /><em className="italic text-sun-600">handing over the keys.</em></>
            )}
          </h2>
          {legalPrimary ? (
            <>
              <p className="mt-5 text-ink-900/80 text-lg leading-relaxed max-w-2xl">
                When you engage CompliSun Legal, every memo, meeting, email, and draft lives in
                your <strong className="text-ink-900">matter file</strong> — built on the same architecture
                we use for our regulated clients. AI calls to <strong className="text-ink-900">Claude</strong>{" "}
                route through <strong className="text-ink-900">AWS Bedrock via PrivateLink</strong>: Anthropic
                has zero access to your privileged communications. Customer-managed{" "}
                <strong className="text-ink-900">KMS</strong> keys. Audit trail in your CloudTrail.
              </p>
              <p className="mt-3 text-ink-900/75 text-base leading-relaxed max-w-2xl">
                That's the difference between a law firm that <em>promises</em> privilege protection and
                one whose <em>architecture</em> makes the privilege un-leakable.
              </p>
            </>
          ) : (
            <>
              {pack.closedSystemsIntro && (
                <p className="mt-5 text-ink-900/80 text-base leading-relaxed max-w-2xl">
                  {pack.closedSystemsIntro}
                </p>
              )}
              <p className="mt-5 text-ink-900/80 text-lg leading-relaxed max-w-2xl">
                Your platform runs entirely inside <strong className="text-ink-900">your AWS VPC</strong>.
                AI calls to <strong className="text-ink-900">Claude</strong> route through{" "}
                <strong className="text-ink-900">AWS Bedrock via PrivateLink</strong> — Anthropic has zero
                access to your prompts, completions, or documents. Customer-managed{" "}
                <strong className="text-ink-900">KMS</strong> keys. Audit trail in <strong className="text-ink-900">your CloudTrail</strong>.
                That's the difference between contractual privacy and architectural privacy.
              </p>
            </>
          )}

          <ul className="mt-6 space-y-2.5">
            {(legalPrimary
              ? [
                  "Every conversation, draft, and decision we've had together — searchable in seconds. Protected knowledge that gets sharper the longer we work with you.",
                  "Attorney-Client Privilege + Attorney Work Product tags on every AI output — auto-applied, never co-mingled with another client's work.",
                  "Sub-processors: AWS (compute) + Anthropic Bedrock (inference). That's it. No data brokers, no fine-tune feed.",
                  "Your matter file is exportable in a single click — you own it on day one, day 365, and after engagement ends.",
                  "Privilege training built into our internal workflows — every CompliSun attorney + analyst is briefed on the architecture, not just the obligation.",
                ]
              : pack.closedSystemsBullets
            ).map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm md:text-base text-ink-900/85 leading-relaxed">
                <Check className="h-4 w-4 text-sun-700 mt-1 flex-shrink-0" strokeWidth={2.5} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Architecture diagram (right) — privilege-framed for legal-primary */}
        <div className="col-span-12 md:col-span-6">
          <div className="border border-ink-900/12 rounded-md bg-white/75 p-5">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700 mb-3">
              {legalPrimary ? "/ how privilege is enforced" : "/ how it's wired"}
            </div>

            {legalPrimary ? (
              <>
                <Layer
                  icon={Scale}
                  title="Privilege tags on every AI output"
                  body="Attorney-Client Privileged · Attorney Work Product — applied at draft time, surfaced on every export."
                />
                <Connector />
                <Layer
                  icon={Cloud}
                  title="Claude via AWS Bedrock + PrivateLink"
                  body="Drafting, redlining, memo review. Anthropic sees nothing — privilege is architectural, not contractual."
                />
                <Connector />
                <Layer
                  icon={Lock}
                  title="Your matter file · your KMS keys"
                  body="Per-client isolation. Customer-managed encryption. Your file lives in YOUR bucket; we hold the key handle, not the key."
                  highlight
                />
                <Connector />
                <Layer
                  icon={Brain}
                  title="Protected knowledge at your fingertips"
                  body="Every memo, meeting note, and draft we've worked on — searchable for you, invisible to the outside world. The longer we work together, the more leveraged your team becomes."
                />
              </>
            ) : (
              <>
                <Layer
                  icon={ShieldCheck}
                  title="Role-based access control"
                  body="One person sees training + policies. Another sees NPPI. You set the gates."
                />
                <Connector />
                <Layer
                  icon={Cloud}
                  title="Claude via AWS Bedrock + PrivateLink"
                  body="Drafting, classification, document review. Anthropic has zero access — architectural, not contractual."
                />
                <Connector />
                <Layer
                  icon={Database}
                  title="Your AWS VPC · your S3 · your KMS keys"
                  body="Per-client isolation. Customer-managed encryption keys. SOC 2 controls inherited."
                  highlight
                />
                <Connector />
                <Layer
                  icon={KeyRound}
                  title="Audit trail in your CloudTrail"
                  body="IAM, VPC isolation, GuardDuty monitoring, complete audit logs — in YOUR account."
                />
              </>
            )}
          </div>
          <p className="mt-3 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 text-center">
            {legalPrimary
              ? "/ protected knowledge · sharper every quarter"
              : "/ SOC 2-grade isolation by architecture, not by promise"}
          </p>
        </div>
      </div>
    </SlideFrame>
  );
}

function Layer({ icon: Icon, title, body, highlight = false }) {
  return (
    <div className={`flex items-start gap-3 rounded-md p-3 ${highlight ? "bg-sun-500/10 border border-sun-500/30" : "bg-cream-100 border border-ink-900/8"}`}>
      <div className={`h-9 w-9 rounded-md grid place-items-center flex-shrink-0 ${highlight ? "bg-sun-500/20" : "bg-white"}`}>
        <Icon className={`h-4 w-4 ${highlight ? "text-sun-700" : "text-ink-900/65"}`} strokeWidth={1.5} />
      </div>
      <div>
        <div className="serif text-base text-ink-900 leading-tight">{title}</div>
        <div className="text-xs text-ink-900/65 mt-0.5 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function Connector() {
  return <div className="h-3 w-px bg-ink-900/15 ml-[19px] my-0.5" />;
}
